import { ActionReducerMapBuilder, AsyncThunk } from '@reduxjs/toolkit';

/**
 * Last request wins, rather than last RESPONSE wins.
 *
 * ---
 *
 * THE PROBLEM THIS SOLVES
 *
 * Every list and statistic in this console is a filtered read: a month, a
 * search string, a status, a page. Change the filter and a new request goes
 * out — but the previous one is still in flight, and nothing says it will come
 * back first. A plain reducer
 *
 *     builder.addCase(getNewcomers.fulfilled, (state, action) => {
 *       state.newcomers = action.payload;
 *     })
 *
 * applies whichever response ARRIVES last, which on a real network is not
 * necessarily the one you asked for last. The dashboard showed this exactly:
 * one page load issued `?month=&year=2026` (the whole year) and `?month=8`
 * (August), and rendered 20 new users or 18 depending on which won the race,
 * with the dropdown reading "August" in both cases.
 *
 * Removing the duplicate request fixed that instance. This guard is what stops
 * the whole class:
 *
 *   - switching a month dropdown twice quickly, where the first query is the
 *     heavier one and lands second;
 *   - typing in a search box faster than the debounce plus round-trip;
 *   - a page that mounts, is navigated away from, and remounted, with the
 *     first mount's response still in the air;
 *   - React 18 StrictMode double-invoking effects in development.
 *
 * All of them are invisible on a fast local network against a seeded database,
 * and all of them appear under real latency.
 *
 * ---
 *
 * HOW IT WORKS
 *
 * `createAsyncThunk` stamps every dispatch with a unique `meta.requestId` that
 * is carried on its pending, fulfilled and rejected actions. So: record the id
 * of the newest request when it starts, and on arrival apply a payload only if
 * its id still matches. A response that has been superseded is dropped rather
 * than written over newer data.
 *
 * The alternative — aborting the superseded request — needs the promise
 * reference held in the component, which spreads request bookkeeping across
 * the UI. This keeps it in the reducer, where the state it protects lives.
 *
 * ---
 *
 * USAGE
 *
 *     builder
 *       .addCase(getNewcomers.fulfilled, applyIfLatest(getNewcomers, (s, p) => {
 *         s.newcomers = p;
 *       }))
 *       // ...every other addCase...
 *
 *     // AFTER the addCase calls: RTK requires matchers to be registered last.
 *     trackLatestRequests(builder, [getNewcomers]);
 *
 * The slice's `initialState` must include `latestRequestId: {}`.
 */

type AnyAsyncThunk = AsyncThunk<any, any, any>;

/** Shape the guard maintains on a slice: thunk type prefix -> newest id. */
export interface WithLatestRequestId {
  latestRequestId: Record<string, string>;
}

/** Slices spread this into their initial state. */
export const latestRequestInitialState: WithLatestRequestId = {
  latestRequestId: {},
};

/**
 * Wraps a fulfilled handler so it runs only for the newest request.
 *
 * Fails OPEN: if nothing has been recorded for this thunk (the slice forgot to
 * call `trackLatestRequests`, or the state was rehydrated without the map),
 * the payload is applied. A guard that silently dropped every response because
 * of a wiring mistake would be a far worse bug than the one it prevents.
 */
export const applyIfLatest =
  <TPayload>(thunk: AnyAsyncThunk, apply: (state: any, payload: TPayload) => void) =>
  (state: any, action: { payload: TPayload; meta: { requestId: string } }) => {
    const latest = state.latestRequestId?.[thunk.typePrefix];

    if (latest !== undefined && latest !== action.meta.requestId) return;

    apply(state, action.payload);
  };

/**
 * Records the newest request id for each of `thunks`.
 *
 * Registered as a matcher rather than a case, because RTK rejects any
 * `addCase` that follows an `addMatcher` — a matcher is the only way to add
 * behaviour to `pending` without disturbing the `addCase(pending)` handlers
 * some slices already use for their loading flags.
 *
 * Call this AFTER every `addCase` in the slice.
 */
export const trackLatestRequests = (
  builder: ActionReducerMapBuilder<any>,
  thunks: AnyAsyncThunk[],
): void => {
  // A lookup from pending action type to the prefix `applyIfLatest` reads,
  // rather than RTK's `isPending(...thunks)`: that overload wants a tuple, so
  // it cannot take an array, and a plain predicate is clearer about what is
  // being matched anyway.
  const prefixByPendingType = new Map(
    thunks.map(thunk => [thunk.pending.type as string, thunk.typePrefix]),
  );

  builder.addMatcher(
    (action: any) => prefixByPendingType.has(action?.type),
    (state: any, action: any) => {
      if (!state.latestRequestId) state.latestRequestId = {};
      state.latestRequestId[prefixByPendingType.get(action.type)!] =
        action.meta.requestId;
    },
  );
};
